import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import { Box, Divider, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import type { ReceiptGroupedMap } from "@/containers/events/types";
import { EventStatsMobileGrid } from "./EventStatsMobileGrid";

const calcRowTotal = (row: { CASH?: number; CHEQUE?: number; ONLINE?: number }) => {
  const { CASH, CHEQUE, ONLINE } = row;
  const cash = Number(CASH || 0);
  const cheque = Number(CHEQUE || 0);
  const online = Number(ONLINE || 0);
  return Intl.NumberFormat("en-IN").format(cash + cheque + online);
};

const formatModeAmount = (amount?: number) =>
  `₹ ${Intl.NumberFormat("en-IN").format(Number(amount || 0))}`;

type ModeTotals = { CASH?: number; CHEQUE?: number; ONLINE?: number };

const TotalRow = ({ values }: { values: Record<string, ModeTotals> }) => {
  const { CASH, CHEQUE, ONLINE } = Object.values(values).reduce<ModeTotals>(
    (acc, value) => {
      const { CASH: cash, CHEQUE: cheque, ONLINE: online } = value;
      acc.CASH = (acc.CASH ?? 0) + Number(cash || 0);
      acc.CHEQUE = (acc.CHEQUE ?? 0) + Number(cheque || 0);
      acc.ONLINE = (acc.ONLINE ?? 0) + Number(online || 0);
      return acc;
    },
    { CASH: 0, ONLINE: 0, CHEQUE: 0 }
  );

  return (
    <TableRow>
      <TableCell>TOTAL</TableCell>
      <TableCell align="center">{formatModeAmount(CASH)}</TableCell>
      <TableCell align="center">{formatModeAmount(CHEQUE)}</TableCell>
      <TableCell align="center">{formatModeAmount(ONLINE)}</TableCell>
      <TableCell align="center">₹ {calcRowTotal({ CASH, CHEQUE, ONLINE })}</TableCell>
    </TableRow>
  );
};

type EventDashboardReceiptDayWiseProps = {
  receiptMap: ReceiptGroupedMap;
  selectedMarkaz: string;
};

export const EventDashboardReceiptDayWise = ({
  receiptMap,
  selectedMarkaz,
}: EventDashboardReceiptDayWiseProps) => {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("md"), { noSsr: true });

  if (!selectedMarkaz) {
    return null;
  }

  const markazRows = receiptMap[selectedMarkaz] ?? {};
  const dayEntries = Object.entries(markazRows);

  const totals = Object.values(markazRows).reduce<ModeTotals>(
    (acc, value) => {
      acc.CASH = (acc.CASH ?? 0) + Number(value.CASH || 0);
      acc.CHEQUE = (acc.CHEQUE ?? 0) + Number(value.CHEQUE || 0);
      acc.ONLINE = (acc.ONLINE ?? 0) + Number(value.ONLINE || 0);
      return acc;
    },
    { CASH: 0, CHEQUE: 0, ONLINE: 0 }
  );

  return (
    <Grid
      sx={{ px: { xs: 0, sm: 1 }, minWidth: 0 }}
      size={{
        lg: 8,
        xs: 12,
      }}
    >
      {isSmall ? (
        <Stack spacing={1.5} sx={{ mb: 5 }}>
          {dayEntries.map(([day, value]) => (
            <Box key={day}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                {day}
              </Typography>
              <EventStatsMobileGrid
                fields={[
                  { label: "Cash", value: formatModeAmount(value.CASH) },
                  { label: "Cheque", value: formatModeAmount(value.CHEQUE) },
                  { label: "Online", value: formatModeAmount(value.ONLINE) },
                  { label: "Total", value: `₹ ${calcRowTotal(value)}` },
                ]}
              />
            </Box>
          ))}
          {dayEntries.length > 0 && (
            <>
              <Divider />
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                  Total
                </Typography>
                <EventStatsMobileGrid
                  fields={[
                    { label: "Cash", value: formatModeAmount(totals.CASH) },
                    { label: "Cheque", value: formatModeAmount(totals.CHEQUE) },
                    { label: "Online", value: formatModeAmount(totals.ONLINE) },
                    { label: "Total", value: `₹ ${calcRowTotal(totals)}` },
                  ]}
                />
              </Box>
            </>
          )}
        </Stack>
      ) : (
        <TableContainer
          component={Paper}
          sx={{ mb: 5, overflowX: "auto", WebkitOverflowScrolling: "touch", width: "100%" }}
        >
          <Table size="small" sx={{ minWidth: 520 }} aria-label="Day wise receipt report">
            <TableHead>
              <TableRow>
                <TableCell>DAY</TableCell>
                <TableCell align="center">CASH</TableCell>
                <TableCell align="center">CHEQUE</TableCell>
                <TableCell align="center">ONLINE</TableCell>
                <TableCell align="center">TOTAL</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {dayEntries.map(([key, value]) => (
                <TableRow key={key} sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                  <TableCell>{key}</TableCell>
                  <TableCell align="center">{formatModeAmount(value.CASH)}</TableCell>
                  <TableCell align="center">{formatModeAmount(value.CHEQUE)}</TableCell>
                  <TableCell align="center">{formatModeAmount(value.ONLINE)}</TableCell>
                  <TableCell align="center">₹ {calcRowTotal(value)}</TableCell>
                </TableRow>
              ))}
              <TotalRow values={markazRows} />
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Grid>
  );
};
